import * as React from 'react';
import { alpha } from '@mui/material';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { toggleButtonClasses } from '@mui/material/ToggleButton';
import { CheckBoxOutlineBlankRounded, CheckRounded, RemoveRounded } from '@mui/icons-material';
import { brand, gray } from '../ThemePrimitives';

export const inputsCustomizations = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
      disableFocusRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        boxSizing: 'border-box',
        transition: 'all 150ms ease-in-out',
        '&:focus-visible': {
          outline: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
          outlineOffset: '2px',
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: 'none',
        borderRadius: theme.shape.borderRadius,
        textTransform: 'none',
        fontWeight: 600,
        variants: [
          {
            props: { size: 'small' },
            style: {
              height: '2.25rem',
              padding: '8px 14px',
              fontSize: '0.8125rem',
            },
          },
          {
            props: { size: 'medium' },
            style: {
              height: '2.5rem',
              padding: '10px 18px',
              fontSize: '0.875rem',
            },
          },
          {
            props: { size: 'large' },
            style: {
              height: '2.75rem',
              padding: '12px 22px',
              fontSize: '0.9375rem',
            },
          },
          {
            props: { color: 'primary', variant: 'contained' },
            style: {
              backgroundColor: brand[500],
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: brand[600],
                boxShadow: `0 4px 12px ${alpha(brand[500], 0.3)}`,
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: `0 2px 6px ${alpha(brand[500], 0.2)}`,
              },
              ...theme.applyStyles('dark', {
                backgroundColor: brand[400],
                '&:hover': {
                  backgroundColor: brand[300],
                  boxShadow: `0 4px 12px ${alpha(brand[400], 0.3)}`,
                },
              }),
            },
          },
          {
            props: { color: 'secondary', variant: 'contained' },
            style: {
              backgroundColor: gray[700],
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: gray[600],
                boxShadow: `0 4px 12px ${alpha(gray[700], 0.3)}`,
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              ...theme.applyStyles('dark', {
                backgroundColor: gray[500],
                '&:hover': {
                  backgroundColor: gray[400],
                },
              }),
            },
          },
          {
            props: { variant: 'outlined', color: 'primary' },
            style: {
              color: brand[600],
              border: '1.5px solid',
              borderColor: brand[200],
              backgroundColor: 'transparent',
              '&:hover': {
                color: brand[700],
                borderColor: brand[400],
                backgroundColor: 'transparent',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              ...theme.applyStyles('dark', {
                color: brand[200],
                borderColor: alpha(brand[400], 0.4),
                '&:hover': {
                  color: brand[100],
                  borderColor: brand[300],
                },
              }),
            },
          },
          {
            props: { variant: 'outlined', color: 'inherit' },
            style: {
              color: gray[700],
              border: '1.5px solid',
              borderColor: gray[200],
              backgroundColor: 'transparent',
              '&:hover': {
                color: gray[900],
                borderColor: gray[400],
                backgroundColor: 'transparent',
                transform: 'translateY(-1px)',
              },
              ...theme.applyStyles('dark', {
                color: gray[300],
                borderColor: gray[600],
                '&:hover': {
                  color: gray[100],
                  borderColor: gray[400],
                },
              }),
            },
          },
          {
            props: { variant: 'text' },
            style: {
              color: gray[600],
              '&:hover': {
                backgroundColor: alpha(gray[100], 0.5),
              },
              '&:active': {
                backgroundColor: alpha(gray[200], 0.3),
              },
              ...theme.applyStyles('dark', {
                color: gray[300],
                '&:hover': {
                  backgroundColor: alpha(gray[700], 0.2),
                },
              }),
            },
          },
          {
            props: { color: 'primary', variant: 'text' },
            style: {
              color: brand[600],
              '&:hover': {
                backgroundColor: alpha(brand[50], 0.5),
              },
              ...theme.applyStyles('dark', {
                color: brand[300],
                '&:hover': {
                  backgroundColor: alpha(brand[900], 0.3),
                },
              }),
            },
          },
        ],
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        textTransform: 'none',
        fontWeight: 500,
        letterSpacing: 0,
        color: theme.palette.text.secondary,
        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
        backgroundColor: 'transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: alpha(theme.palette.action.hover, 0.4),
          borderColor: alpha(theme.palette.divider, 0.3),
        },
        variants: [
          {
            props: { size: 'small' },
            style: {
              width: '2rem',
              height: '2rem',
              padding: '0.25rem',
              [`& .${svgIconClasses.root}`]: { fontSize: '0.875rem' },
            },
          },
        ],
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        padding: '2px',
        backgroundColor: alpha(theme.palette.primary.light, 0.03),
        [`& .${toggleButtonGroupClasses.selected}`]: {
          color: brand[500],
        },
        ...theme.applyStyles('dark', {
          backgroundColor: alpha(gray[800], 0.3),
          [`& .${toggleButtonGroupClasses.selected}`]: {
            color: brand[300],
          },
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '8px 14px',
        textTransform: 'none',
        borderRadius: theme.shape.borderRadius,
        fontWeight: 500,
        fontSize: '0.8125rem',
        border: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
        },
        [`&.${toggleButtonClasses.selected}`]: {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          color: brand[500],
        },
        ...theme.applyStyles('dark', {
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          },
          [`&.${toggleButtonClasses.selected}`]: {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
            color: brand[300],
          },
        }),
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: (
        <CheckBoxOutlineBlankRounded sx={{ color: 'hsla(210, 0%, 0%, 0.0)' }} />
      ),
      checkedIcon: <CheckRounded sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRounded sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 8,
        height: 18,
        width: 18,
        borderRadius: theme.shape.borderRadius,
        border: '1px solid',
        borderColor: alpha(gray[300], 0.6),
        backgroundColor: alpha(gray[100], 0.2),
        transition: 'all 150ms ease-in-out',
        '&:hover': {
          borderColor: brand[300],
        },
        '&.Mui-focusVisible': {
          outline: `2px solid ${alpha(brand[500], 0.4)}`,
          outlineOffset: '2px',
          borderColor: brand[400],
        },
        '&.Mui-checked': {
          color: 'white',
          backgroundColor: brand[500],
          borderColor: brand[500],
          '&:hover': {
            backgroundColor: brand[600],
          },
        },
        ...theme.applyStyles('dark', {
          borderColor: alpha(gray[600], 0.6),
          backgroundColor: alpha(gray[800], 0.3),
          '&:hover': {
            borderColor: brand[400],
          },
          '&.Mui-checked': {
            backgroundColor: brand[600],
            borderColor: brand[600],
            '&:hover': {
              backgroundColor: brand[700],
            },
          },
        }),
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: 'none',
        borderRadius: theme.shape.borderRadius,
      }),
      input: ({ theme }) => ({
        '&::placeholder': {
          opacity: 0.6,
          color: gray[500],
        },
        ...theme.applyStyles('dark', {
          '&::placeholder': {
            color: gray[400],
          },
        }),
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: 0,
      },
      root: ({ theme }) => ({
        padding: '4px 12px',
        color: theme.palette.text.primary,
        borderRadius: theme.shape.borderRadius,
        border: '1px solid',
        borderColor: alpha(gray[300], 0.5),
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        transition: 'all 150ms ease-in-out',
        '&:hover': {
          borderColor: brand[300],
          backgroundColor: alpha(theme.palette.background.paper, 1),
        },
        [`&.${outlinedInputClasses.focused}`]: {
          borderColor: brand[500],
          borderWidth: '2px',
          backgroundColor: alpha(theme.palette.background.paper, 1),
          padding: 'calc(4px - 0.5px) 12px',
        },
        ...theme.applyStyles('dark', {
          borderColor: alpha(gray[700], 0.5),
          backgroundColor: alpha(gray[900], 0.3),
          '&:hover': {
            borderColor: brand[400],
            backgroundColor: alpha(gray[900], 0.5),
          },
          [`&.${outlinedInputClasses.focused}`]: {
            borderColor: brand[500],
            backgroundColor: alpha(gray[900], 0.5),
          },
        }),
        [`& .MuiAutocomplete-endAdornment`]: {
          right: 0,
        },
        variants: [
          {
            props: { size: 'small' },
            style: {
              height: 'auto',
              minHeight: '36px',
            },
          },
        ],
      }),
      notchedOutline: {
        border: 'none',
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.grey[500],
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        typography: theme.typography.caption,
        marginBottom: 6,
        fontWeight: 500,
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
      }),
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: () => ({
        '& .MuiInputBase-root': {
          padding: '2px 8px',
          minHeight: '32px',
        },
        '& .MuiAutocomplete-input': {
          padding: '0 !important',
          fontSize: '0.8125rem',
        },
      }),
      paper: ({ theme }) => ({
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        borderRadius: theme.shape.borderRadius,
        marginTop: '4px',
        backgroundColor: alpha(theme.palette.background.paper, 0.98),
        backdropFilter: 'blur(8px)',
        ...theme.applyStyles('dark', {
          backgroundColor: alpha(gray[900], 0.98),
        }),
      }),
    },
  },
};
